<template>
  <el-input
    ref="inputRef"
    v-model="value"
    :class="`${bem.b()} ${prepareClassNames()}`"
    :style="{
      ...prepareStyles(),
    }"
    v-bind="customAttrs"
    :type="type === 'number' ? 'text' : type"
  >
    <template v-for="name in slots" #[name]="scope">
      <slot :name="name" v-bind="scope" />
    </template>
  </el-input>
</template>
<script setup lang="ts">
import { computed, useSlots, watch, ref, nextTick} from 'vue'
import { createNameSpace } from '@el-plus/utils/bem'
import { inputProps, inputEmits, inputEmitsKeys } from './input'
import type { InputProps, InputEmits } from './input'
import {
  prepareProps,
  prepareEvents,
  prepareClassNames,
  prepareStyles,
} from '@el-plus/utils/props'
defineOptions({
  name: 'EpInput',
  inheritAttrs: false,
})
const bem = createNameSpace('input')
const props = defineProps(inputProps)
const emit = defineEmits(inputEmits)
const slots = computed(() => {
  return Object.keys(useSlots())
})
let isBluring = false
let isFocusing = false
const customAttrs = computed(() => {
  return {
    ...prepareProps(props, ['modelValue']),
    ...prepareEvents<InputEmits>(emit, inputEmitsKeys),
    placeholder: props.disabled ? '' : props.placeholder,
    onClear: () => {
      emit('clear')
      if (!props.clearableFocus) {
        nextTick(() => {
          inputRef.value?.blur?.()
        })
      }
    },
    onFocus: (e: FocusEvent) => {
      isFocusing = true
      if (props.thousand) {
        ;(e.target as HTMLInputElement).value = value.value
          ? String(value.value)
          : ''
      }
      emit('focus', e)
    },
    onBlur: (e: FocusEvent) => {
      if (props.type === 'number') {
        let val = value.value as string
        if (props.nonzero && +val === 0) {
          val = ''
        }
        if (val && val[val.length - 1] === '.') {
          val = String(val).slice(0, -1)
        }

        if (val && val === '-') {
          val = ''
        }
        value.value = val ? +val : val
        ;(e.target as HTMLInputElement).value =value.value? toThousand(value.value):''
      }
      isBluring = true
      isFocusing = false
      emit('blur', e)
    },
  }
})
const value = defineModel<InputProps['modelValue']>()
const inputRef = ref()
const toThousand = (val: string | number) => {
  if (props.thousand) {
    return val ? window.hx.thousand(val) : val
  }
  return val
}
watch(
  () => value.value,
  (val) => {
    if (props.type === 'number' && !isFocusing) {
      nextTick(() => {
        inputRef.value.input.value = val?toThousand(val as number):''
      })
    }
    if (isBluring) {
      isBluring = false
      return
    }
    if (!isFocusing) {
      return
    }
    if (props.type === 'number') {
      if (typeof val === 'number') {
        val = '' + val
      }
      if (typeof val === 'string') {
        // 校验一次是否0+数字开头，这种情况直接把0去除
        const zeroReg = new RegExp('^[-+]?0[0-9]\\d*')
        if (zeroReg.test(val)) {
          val = val.replace('0', '')
        }
        const reg = new RegExp('^([-+])?\\d*(\\.?\\d*)', 'g')
        val = val.match(reg) ? val.match(reg)![0] : ''

        // 限制小数位数
        if (props.radix || props.radix === 0) {
          const reg =
            props.radix === 0
              ? new RegExp('^([-+])?\\d*', 'g')
              : new RegExp(`^([-+])?\\d*(\\.?\\d{0,${props.radix}})`, 'g')
          val = val.match(reg) ? val.match(reg)![0] : ''
        }
        if ((props.max || props.max === 0) && +val > props.max) {
          val = props.max
        }
        // 最大最小值
        if (
          (props.min || props.min === 0) &&
          (val || val === 0) &&
          +val < props.min
        ) {
          val = props.min
        }
      }
    } else if (props.type === 'text') {
      val = (val as string).trim()
    }
    value.value = val
  },
  {
    immediate: true,
  },
)
</script>
